/**
 * Coast — the single definition of the seabed.
 *
 * THE ARENA
 * A bay. Cross-shore is Z; along-shore is X, and the shoreline sweeps seaward at both ends
 * until it has crossed the whole playable band, which closes the water into a lagoon roughly
 * 860 u long. That is a headland, not a wall: the land you cannot swim past is land you can
 * see, and the reef and the beach curve round with it because every zone is defined in
 * shore-relative coordinates.
 *
 * Enclosing it is a multiplayer decision as much as an aesthetic one. An unbounded coast lets
 * two players end up four thousand units apart with nothing drawing them back together, and a
 * shared world nobody shares is not one.
 *
 * The cross-shore profile:
 *
 *     z < 0      dry beach, above the waterline
 *     0 -  12    intertidal; ankle-deep, wavelets, wet sand
 *    12 -  48    lagoon, deepening to ~14 u — the open swimming water
 *    48 - 102    back-reef and reef flat, back up to ~6 u — this is where the reef lives
 *   102 - 122    fore-reef slope
 *   122 - 150    the drop-off, plunging past 55 u into the blue
 *
 * so the playable band is ~150 u across, roughly two and a half screens at this camera.
 *
 * WHY IT IS WRITTEN TWICE
 * The seafloor shader displaces the mesh with this field and the CPU places props on it. If
 * the two ever disagree, coral floats or sinks. They are kept adjacent in this one file, in
 * the same order, with the same constants, precisely so a change to one is impossible to
 * make without seeing the other. Summed sines rather than hash noise for the same reason a
 * float and a double must agree on the answer.
 */
import { OCEAN } from '../data/tuning.ts';

/* ------------------------------------------------------------------ constants shared by both
   Kept as literals in the GLSL below rather than uniforms: they are level design, not tuning,
   and a uniform you never change is a uniform you can forget to upload. */
export const COAST = {
  /** world Z where the profile crosses the waterline; the beach is behind it */
  shoreZ: 0,
  /** last Z with a swimmable bed; past here it is the drop-off */
  edgeZ: 138,
  /** cross-shore window the reef occupies */
  reefFrom: 54,
  reefTo: 120,
  /** where offshore relief begins, and how far out the seamounts run before the mesh ends */
  offshoreFrom: 148,
  offshoreTo: 300,
  /** shallowest water a shark will swim in, u */
  minSwimDepth: 3.4,
  /** half the lagoon's along-shore length; the headlands close it at +/- this */
  lagoonHalf: 430,
  /** where the headlands begin to swing seaward */
  headlandFrom: 300,
} as const;

export const COAST_GLSL = /* glsl */ `
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash12(i),                 hash12(i + vec2(1.0, 0.0)), u.x),
             mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), u.x), u.y);
}

// Long-wavelength relief, on top of the profile. 37-55 u wavelengths: mounds and hollows a
// few body-lengths across, which is the scale that reads as terrain from this camera.
float dune(vec2 p) {
  return (sin(p.x * 0.037 + 1.7) * 0.55
        + sin(p.y * 0.029 - 0.6) * 0.62
        + sin(p.x * 0.021 + p.y * 0.017 + 2.3) * 0.50
        + sin(p.x * -0.013 + p.y * 0.024 - 1.1) * 0.38
        + sin(p.x * 0.091 - p.y * 0.067 + 0.4) * 0.42
        + sin(p.x * 0.058 + p.y * 0.112 - 2.2) * 0.34
        + sin(p.x * -0.145 + p.y * 0.083 + 1.9) * 0.22) * 0.33;
}

/* The shoreline is not a straight line, and it does not run for ever.

   The meander (200-1000 u wavelengths) shifts the whole cross-shore profile in Z as you
   travel, which is what makes the coast wander. The HEADLAND term is what closes the bay: it
   subtracts enough shift at both ends to carry the shore right across the playable band, so
   past about 430 u there is no water at any Z. Because every zone downstream is defined in
   these shore-relative coordinates, the beach, the reef and the drop-off all curve round the
   ends together — the arms of the bay are made of reef, not of an invisible barrier. */
float shoreShift(float x) {
  float meander = sin(x * 0.0062 - 0.4) * 13.0
                + sin(x * 0.0135 + 1.2) *  9.0
                + sin(x * 0.0310 + 2.7) *  4.5;
  float headland = smoothstep(300.0, 430.0, abs(x)) * 190.0;
  return meander - headland;
}

/* The cross-shore profile: beach, lagoon, reef flat, fore-reef, drop-off.

   Layered mixes rather than a table lookup. Each smoothstep hands the curve on to the next
   control depth as z passes its band, so the whole profile is seven lines with no branching,
   no array indexing, and an exact CPU mirror. */
float coastProfile(float z) {
  float y = 37.0;                                        // dry beach, inland
  y = mix(y, 26.4, smoothstep(-16.0,  12.0, z));         // beach face through the tide line
  y = mix(y, 14.0, smoothstep( 12.0,  48.0, z));         // lagoon — the deep part of the walk
  y = mix(y, 21.0, smoothstep( 48.0,  68.0, z));         // back-reef, rising to the crest
  // The reef flat is shallow but it has to stay SWIMMABLE: a real one is ankle-deep, and at
  // 1 u of water an 8 u shark riding 3 u off the bed simply cannot cross its own reef.
  y = mix(y, 20.4, smoothstep( 68.0, 102.0, z));         // reef flat
  y = mix(y,  6.0, smoothstep(102.0, 122.0, z));         // fore-reef slope
  y = mix(y, -26.0, smoothstep(122.0, 150.0, z));        // the drop-off
  return y;
}

/* Reef passes: the channels a lagoon drains through, cut clean across the crest. Narrow and
   far apart because the threshold only catches the top of the summed field — a reef with
   evenly spaced gaps reads as a fence. */
float reefPass(float x) {
  float f = sin(x * 0.0089 + 0.7) * 0.62
          + sin(x * 0.0041 - 2.1) * 0.55
          + sin(x * 0.0173 + 1.9) * 0.33;
  return smoothstep(0.72, 1.15, f);
}

// Where rock outcrops within the reef band. Patches are 72-200 u across: big enough to swim
// the length of, small enough that open sand is always somewhere in frame.
float reefField(vec2 p) {
  return (sin(p.x * 0.045 + 1.1) * 0.62
        + sin(p.y * 0.038 - 2.3) * 0.58
        + sin(p.x * 0.031 + p.y * 0.027 + 0.4) * 0.74
        + sin(p.x * -0.062 + p.y * 0.051 - 1.7) * 0.40
        + sin(p.x * 0.087 - p.y * 0.074 + 2.8) * 0.26) * 0.38;
}

/* 0 on sand, 1 on reef rock. Gated three ways: the cross-shore band (coral needs light and
   surf, so it builds on the crest), a weaker lagoon window for the scattered patch reefs
   behind the crest, and the passes, which are swept clear by the current running through
   them and are therefore bare. */
float reefMaskAt(vec2 p, float zs) {
  float crest  = smoothstep(54.0, 72.0, zs) * (1.0 - smoothstep(100.0, 120.0, zs));
  float lagoon = smoothstep(20.0, 33.0, zs) * (1.0 - smoothstep(38.0, 50.0, zs)) * 0.42;
  return smoothstep(0.07, 0.39, reefField(p))
       * max(crest, lagoon)
       * (1.0 - reefPass(p.x) * 0.90);
}

float reefRugosity(vec2 p) {
  return sin(p.x *  0.310 + p.y * 0.220 + 0.7) * 0.42
       + sin(p.x * -0.190 + p.y * 0.370 - 1.9) * 0.36
       + sin(p.x *  0.420 - p.y * 0.310 + 2.4) * 0.22;
}

/* World Y of the seabed. The one function everything else agrees on. */
/* ---- offshore ------------------------------------------------------------------------------
   Past the drop-off the bed used to be a flat plain at -26, which is honest bathymetry and
   terrible level design: a fifty-unit cliff down onto nothing, with no reason to go over the
   edge and nothing to steer by once you had. Seamounts fix both. They rise from the abyss on the
   same summed-sine trick the reef uses, and the tallest of them break the surface as islands —
   so the deep has landmarks, the horizon has silhouettes, and swimming out is a journey between
   things rather than a swim into fog.

   SUMMED SINES, never hash noise. Every field in this file is evaluated twice — once in GLSL at
   float precision for the mesh, once here in double for physics — and anything discontinuous
   would put the two on opposite sides of a threshold and drop the shark through the sea floor. */
float seamountField(vec2 p) {
  /* Periods of 250-480 u. The first cut used 0.0062, a period of 1013 u against a bay 860 u
     wide — so barely one cycle fitted and every peak landed on the headlands, leaving a
     completely empty plain between them. The frequency has to be read against the size of the
     thing it is shaping, not chosen for how a sine looks on its own. */
  return sin(p.x * 0.0248 + 0.9) * 0.66
       + sin(p.y * 0.0193 - 1.7) * 0.58
       + sin((p.x + p.y) * 0.0131 + 2.4) * 0.71
       + sin((p.x - p.y) * 0.0167 - 0.6) * 0.49;
}

/* Two thresholds on one field, which is what makes islands rare without a second generator:
   the broad shoulder is a seamount rising into swimmable water, and only the very peaks clear
   the surface. An island is therefore always the summit of a mount you can swim around. */
float seamountAt(vec2 p, float zs) {
  float band = smoothstep(140.0, 178.0, zs);
  if (band < 0.001) return 0.0;
  float f = seamountField(p);
  float mount = smoothstep(0.55, 1.70, f);
  float isle  = smoothstep(1.62, 2.15, f);
  /* Rugosity on the flanks only. A mount whose summit is as lumpy as its sides reads as a pile
     of rubble; real ones are scoured smooth on top by the current. */
  float flank = mount * (1.0 - isle);
  return band * (mount * 44.0 + isle * 26.0 + flank * reefRugosity(p) * 1.6);
}

float bedY(vec2 p, float duneAmp, float reefRise) {
  float zs = p.y + shoreShift(p.x);
  float y = coastProfile(zs);

  // A pass cuts the crest back down toward lagoon depth, so it is a real channel you can
  // swim through rather than a colour change on top of the same reef.
  float band = smoothstep(56.0, 74.0, zs) * (1.0 - smoothstep(104.0, 124.0, zs));
  y -= reefPass(p.x) * band * 8.5;

  // Dune relief fades out over the drop-off: metre-scale mounds on a 50 u cliff read as
  // noise, and the cliff is the silhouette that has to stay clean.
  y += dune(p) * duneAmp * (1.0 - smoothstep(108.0, 138.0, zs));
  y += reefMaskAt(p, zs) * reefRise * (1.0 + reefRugosity(p) * 0.34);
  y += seamountAt(p, zs);
  return y;
}
`;

/* ============================================================================================
   CPU MIRROR — same order, same constants, same results as the GLSL above.
   ============================================================================================ */

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}
const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

export function dune(x: number, z: number): number {
  return (
    (Math.sin(x * 0.037 + 1.7) * 0.55 +
      Math.sin(z * 0.029 - 0.6) * 0.62 +
      Math.sin(x * 0.021 + z * 0.017 + 2.3) * 0.5 +
      Math.sin(x * -0.013 + z * 0.024 - 1.1) * 0.38 +
      Math.sin(x * 0.091 - z * 0.067 + 0.4) * 0.42 +
      Math.sin(x * 0.058 + z * 0.112 - 2.2) * 0.34 +
      Math.sin(x * -0.145 + z * 0.083 + 1.9) * 0.22) *
    0.33
  );
}

export function shoreShift(x: number): number {
  const meander =
    Math.sin(x * 0.0062 - 0.4) * 13 +
    Math.sin(x * 0.0135 + 1.2) * 9 +
    Math.sin(x * 0.031 + 2.7) * 4.5;
  const headland = smoothstep(300, 430, Math.abs(x)) * 190;
  return meander - headland;
}

export function coastProfile(z: number): number {
  let y = 37;
  y = mix(y, 26.4, smoothstep(-16, 12, z));
  y = mix(y, 14.0, smoothstep(12, 48, z));
  y = mix(y, 21.0, smoothstep(48, 68, z));
  y = mix(y, 20.4, smoothstep(68, 102, z));
  y = mix(y, 6.0, smoothstep(102, 122, z));
  y = mix(y, -26.0, smoothstep(122, 150, z));
  return y;
}

export function reefPass(x: number): number {
  const f =
    Math.sin(x * 0.0089 + 0.7) * 0.62 +
    Math.sin(x * 0.0041 - 2.1) * 0.55 +
    Math.sin(x * 0.0173 + 1.9) * 0.33;
  return smoothstep(0.72, 1.15, f);
}

export function reefField(x: number, z: number): number {
  return (
    (Math.sin(x * 0.045 + 1.1) * 0.62 +
      Math.sin(z * 0.038 - 2.3) * 0.58 +
      Math.sin(x * 0.031 + z * 0.027 + 0.4) * 0.74 +
      Math.sin(x * -0.062 + z * 0.051 - 1.7) * 0.4 +
      Math.sin(x * 0.087 - z * 0.074 + 2.8) * 0.26) *
    0.38
  );
}

export function reefRugosity(x: number, z: number): number {
  return (
    Math.sin(x * 0.31 + z * 0.22 + 0.7) * 0.42 +
    Math.sin(x * -0.19 + z * 0.37 - 1.9) * 0.36 +
    Math.sin(x * 0.42 - z * 0.31 + 2.4) * 0.22
  );
}

/** 0 on sand, 1 on reef rock */
export function reefMask(x: number, z: number): number {
  const zs = z + shoreShift(x);
  const crest = smoothstep(54, 72, zs) * (1 - smoothstep(100, 120, zs));
  const lagoon = smoothstep(20, 33, zs) * (1 - smoothstep(38, 50, zs)) * 0.42;
  return smoothstep(0.07, 0.39, reefField(x, z)) * Math.max(crest, lagoon) * (1 - reefPass(x) * 0.9);
}

/** the offshore relief; see the GLSL above, which this mirrors exactly */
export function seamountField(x: number, z: number): number {
  return (
    Math.sin(x * 0.0248 + 0.9) * 0.66 +
    Math.sin(z * 0.0193 - 1.7) * 0.58 +
    Math.sin((x + z) * 0.0131 + 2.4) * 0.71 +
    Math.sin((x - z) * 0.0167 - 0.6) * 0.49
  );
}

/** how far the bed rises above the abyssal plain at (x, z) */
export function seamountAt(x: number, z: number, zs: number): number {
  const band = smoothstep(140, 178, zs);
  if (band < 0.001) return 0;
  const f = seamountField(x, z);
  const mount = smoothstep(0.55, 1.70, f);
  const isle = smoothstep(1.62, 2.15, f);
  const flank = mount * (1 - isle);
  return band * (mount * 44 + isle * 26 + flank * reefRugosity(x, z) * 1.6);
}

/** world Y of the seabed at (x, z) */
export function bedHeight(x: number, z: number): number {
  const zs = z + shoreShift(x);
  let y = coastProfile(zs);
  const band = smoothstep(56, 74, zs) * (1 - smoothstep(104, 124, zs));
  y -= reefPass(x) * band * 8.5;
  y += dune(x, z) * OCEAN.duneAmplitude * (1 - smoothstep(108, 138, zs));
  y += reefMask(x, z) * OCEAN.reefRise * (1 + reefRugosity(x, z) * 0.34);
  y += seamountAt(x, z, zs);
  return y;
}

/** where a run starts: mid-lagoon, with the reef crest a short swim seaward */
export const SPAWN: { x: number; z: number } = { x: 0, z: 34 };

/** metres of water over the bed; negative on dry beach */
export function waterDepth(x: number, z: number): number {
  return OCEAN.surfaceHeight - bedHeight(x, z);
}

/** surface normal of the bed, from a finite difference of the same field */
export function bedNormal(
  x: number,
  z: number,
  out: { x: number; y: number; z: number },
  epsilon = 0.5,
): void {
  const h0 = bedHeight(x, z);
  const dx = (bedHeight(x + epsilon, z) - h0) / epsilon;
  const dz = (bedHeight(x, z + epsilon) - h0) / epsilon;
  const len = Math.hypot(dx, 1, dz);
  out.x = -dx / len;
  out.y = 1 / len;
  out.z = -dz / len;
}

/**
 * Push a position into swimmable water.
 *
 * The old version solved only for Z, which was correct while the only boundaries were the
 * beach behind and the drop-off in front. A bay has boundaries in every direction, so this
 * climbs the DEPTH GRADIENT instead: whatever is shallow — the beach, a headland, a reef
 * spur — the escape is always toward deeper water, and the same six lines handle all of them.
 *
 * It costs nothing when it is not needed: the first depth test returns immediately in open
 * water, which is where the shark is almost always.
 */
export function pushToWater(
  x: number,
  z: number,
  out: { x: number; z: number },
  minDepth = COAST.minSwimDepth,
): void {
  out.x = x;
  out.z = z > COAST.edgeZ ? COAST.edgeZ : z;
  for (let i = 0; i < 12; i++) {
    const d = waterDepth(out.x, out.z);
    if (d >= minDepth) return;
    const e = 1.5;
    const gx = waterDepth(out.x + e, out.z) - waterDepth(out.x - e, out.z);
    const gz = waterDepth(out.x, out.z + e) - waterDepth(out.x, out.z - e);
    const len = Math.hypot(gx, gz) || 1;
    // Step proportional to the shortfall, so a long way inland is escaped in a few iterations
    // rather than crawling out one unit at a time.
    const step = Math.min(7, (minDepth - d) * 1.6 + 1);
    out.x += (gx / len) * step;
    out.z += (gz / len) * step;
    if (out.z > COAST.edgeZ) out.z = COAST.edgeZ;
  }
}

/** 0 in the middle of the bay, 1 at the headlands — for anything that wants to turn back */
export function lagoonEdge(x: number): number {
  return smoothstep(COAST.headlandFrom - 40, COAST.lagoonHalf - 30, Math.abs(x));
}
